import * as React from "react"
import { Button, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown } from "react-icons/fa"
import { Subscription } from "rxjs"
import SearchBar from "../SearchBar"
import SearchField from "../../api/SearchField"
import FormatSSN from "../../formatters/FormatSSN"
import { PoolMemberCheck, PoolCompanyInfoGet, PoolMemberAdd } from "../../api/Randoms/PoolMemberProcess"
import { PoolCompanyInfo, PoolRegulatoryMode } from "../../api/Randoms/RandomsModel/PoolCompanyInfo"
import CheckPoolMember from "../../api/Randoms/RandomsModel/CheckPoolMember"
import PoolMemberData from "../../api/Randoms/RandomsModel/PoolMemberData"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import AutoFilterRegion from "../../api/Regions/Model/AutoFilterRegion"
import AutoFilterRegionsGet from "../../api/Regions/AutoFilterRegionsGet"
import AutoFilterLocation from "../../api/Location/LocationModel/AutoFilterLocation"
import AutoFilterLocationsGet from "../../api/Location/AutoFilterLocationsGet"
import Category from "../../api/LabService/LabServiceModel/EmployeeCategory"
import EmployeeCategoriesGetObservable from "../../api/LabService/EmployeeCategoriesGet"
import CompanyInput from "../FormattedInputs/CompanyInput"
import { UpdateMethodID } from "../../config/Constants"
import { GetUserId } from "../../storage/UserId"
import StateSelect from "../FormattedInputs/StateSelect"
import State from "../../api/OrderRequests/OrderRequestsModel/State"
import { StateObservable } from "../../api/OrderRequests/OrderRequestsConstraintGet"

interface AddPoolMemberModalProps {
  show: boolean
  poolID: number
  accountType: string
  onAdd: () => void
  onCancel: () => void
}

interface AddPoolMemberModalPersonnelCheckData {
  id?: string
  idType?: string
  isPersonnelFound?: boolean
  checkMember?: CheckPoolMember
}

interface AddPoolMemberModalValidity {
  isCompanyValid: boolean
  isLastNameValid: boolean
  isFirstNameValid: boolean
  isStateValid: boolean
}

export interface AddPoolMemberModalData {
  firstName: string
  lastName: string
  selectedCompany?: AutoFilterCompany
  selectedRegion?: AutoFilterRegion
  selectedLocation?: AutoFilterLocation
  selectedRegulatoryMode?: PoolRegulatoryMode
  selectedCategory?: Category
  state?: State
  stateCode?: string
}

interface AddPoolMemberModalState extends AddPoolMemberModalData, AddPoolMemberModalValidity, AddPoolMemberModalPersonnelCheckData {
  isLoading: boolean
  isSaving: boolean
  showAddPersonnelPanel: boolean
  poolCompanyData?: PoolCompanyInfo
  isCompaniesLoading: boolean
  isRegionsLoading: boolean
  isLocationsLoading: boolean
  isCategoriesLoading: boolean
  companies: AutoFilterCompany[]
  regions: AutoFilterRegion[]
  locations: AutoFilterLocation[]
  regulatoryModes: PoolRegulatoryMode[]
  categories: Category[]
  states: State[]
  statesLoading: boolean
  state?: State
  stateCode?: string
}

export default class AddPoolMemberModal extends React.Component<AddPoolMemberModalProps, AddPoolMemberModalState> {
  emptyData: AddPoolMemberModalData = {
    firstName: "",
    lastName: "",
    selectedRegulatoryMode: undefined,
    selectedCategory: undefined,
    state: undefined,
    stateCode: undefined
  }
  positiveValidity: AddPoolMemberModalValidity = { isCompanyValid: true, isLastNameValid: true, isFirstNameValid: true, isStateValid: true }
  personnelCheckEmptyData: AddPoolMemberModalPersonnelCheckData = { idType: undefined, id: undefined, isPersonnelFound: false, checkMember: undefined }
  state: AddPoolMemberModalState = {
    ...this.emptyData,
    ...this.positiveValidity,
    ...this.personnelCheckEmptyData,
    showAddPersonnelPanel: false,
    isLoading: false,
    isSaving: false,
    isCompaniesLoading: false,
    isRegionsLoading: false,
    isLocationsLoading: false,
    isCategoriesLoading: false,
    companies: [],
    regions: [],
    locations: [],
    regulatoryModes: [],
    categories: [],
    states: [],
    statesLoading: false
  }
  idTypes: SearchField[] = [
    { ID: "AlternateID", Name: "Alternate ID" },
    { ID: "DriverID", Name: "Driver ID" },
    { ID: "EmployerID", Name: "Employer ID" },
    { ID: "SSN", Name: "SSN" },
    { ID: "FirstName", Name: "First Name" },
    { ID: "LastName", Name: "Last Name" },
  ]

  companyTypeahead = React.createRef<Typeahead<AutoFilterCompany>>()
  regionTypeahead = React.createRef<Typeahead<AutoFilterRegion>>()
  locationTypeahead = React.createRef<Typeahead<AutoFilterLocation>>()
  regulatoryModeTypeahead = React.createRef<Typeahead<PoolRegulatoryMode>>()
  categoryTypeahead = React.createRef<Typeahead<Category>>()

  subscriptions = new Subscription()

  componentDidMount() {
    this.setState({ isCompaniesLoading: true })
    this.subscriptions.add(
      PoolCompanyInfoGet(this.props.poolID).subscribe(
      (data: PoolCompanyInfo) => {
        const companies: AutoFilterCompany[] = data.CompanyList.length > 0
          ? data.CompanyList.map((company) => {return { AccountID: company.ID, Name: company.Description, Description: company.Description, PrimaryIDType: "SSN" } as AutoFilterCompany})
          : [{ AccountID: data.CompanyID, Name: data.CompanyName, Description: data.CompanyName, PrimaryIDType: data.PrimaryIDType }]
        const selectedCompany = companies.length > 1 ? undefined : companies[0]
        this.setState({ companies: companies, selectedCompany: selectedCompany, regulatoryModes: data.RegulatoryModes, poolCompanyData: data, isCompaniesLoading: false, idType: selectedCompany?.PrimaryIDType || "SSN" })

        if (data.CompanyList.length === 1) {
          this.loadAllRegions(selectedCompany)
          this.loadAllLocations(selectedCompany)
        }

        if (data.HasRegModeBlock && data.RegulatoryModes.length === 1) {
          this.setState({ selectedRegulatoryMode: data.RegulatoryModes[0] })
          this.loadCategories(data.RegulatoryModes[0])
        }
      },
      () => this.setState({ isCompaniesLoading: false })
    ))
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  loadAllRegions = (company?: AutoFilterCompany) => {
    this.setState({ isRegionsLoading: true })
    this.subscriptions.add(
      AutoFilterRegionsGet(company?.AccountID || 0, "").subscribe(
        (data: AutoFilterRegion[]) => this.setState({ regions: data, isRegionsLoading: false }),
        () => this.setState({ isRegionsLoading: false })
      )
    )
  }

  loadAllLocations = (company?: AutoFilterCompany, region?: AutoFilterRegion) => {
    this.setState({ isLocationsLoading: true })
    this.subscriptions.add(
      AutoFilterLocationsGet(company?.AccountID || 0, 0, "").subscribe(
        (data: AutoFilterLocation[]) => this.setState({ locations: data, isLocationsLoading: false }),
        () => this.setState({ isLocationsLoading: false })
      )
    )
  }

  loadCategories = (regulatoryMode: PoolRegulatoryMode) => {
    this.setState({ isCategoriesLoading: true })
    this.subscriptions.add(
      EmployeeCategoriesGetObservable(regulatoryMode.ID).subscribe(
        categories => this.setState({ categories: categories, isCategoriesLoading: false }),
        () => this.setState({ isCategoriesLoading: false })
      )
    )
  }

  loadStates = () => {
    this.setState({ statesLoading: true })
    this.subscriptions.add(
      StateObservable().subscribe(
        (results: State[]) => this.setState({ states: results, statesLoading: false }),
        () => { this.setState({ statesLoading: false }) }
      )
    )
  }

  savePoolMember = () => {
    const primaryIDType = this.state.idType || ""
    const id = this.state.id || ""
    const params: PoolMemberData = {
      PoolID: this.props.poolID,
      Employer: primaryIDType === "EmployerID" ? id : "",
      Driver: primaryIDType === "DriverID" ? id : "",
      SSN: primaryIDType === "SSN" ? id : "",
      Alternate: primaryIDType === "AlternateID" ? id : "",
      PrimaryIDType: primaryIDType,
      CompanyPrimaryID: primaryIDType,
      PrimaryIDValue: id,
      CompanyID: this.state.selectedCompany?.AccountID || 0,
      Company: this.state.selectedCompany?.Name || "",
      RegionID: this.state.selectedRegion?.Code || "",
      Region: this.state.selectedRegion?.Name || "",
      LocationID: this.state.selectedLocation?.AccountID || 0,
      Location: this.state.selectedLocation?.Name || "",
      FirstName: this.state.firstName.replace(/^\s+|\s+$/gm, "").replace(/\s+/g, " "),
      LastName: this.state.lastName.replace(/^\s+|\s+$/gm, "").replace(/\s+/g, " "),
      ConsortiumPoolCode: "",
      IsConsortiumPool: "",
      EmployeeCategory: this.state.selectedCategory?.Name || null,
      EmployeeCategoryID: this.state.selectedCategory?.EmployeeCategoryID || null,
      Inactive: this.state.checkMember?.Inactive || false,
      PersonID: this.state.checkMember?.PersonID || "",
      RegulatoryMode: this.state.selectedRegulatoryMode?.Description || null,
      RegulatoryModeID: this.state.selectedRegulatoryMode?.ID || null,
      TerminationDate: null,
      UpdateMethodID: UpdateMethodID,
      UserLoginID: GetUserId(),
      searchString: id,
      DriverLICStateOfIssue: this.state.stateCode || ""
    }

    if (this.state.poolCompanyData) {
      params.ConsortiumPoolCode = this.state.poolCompanyData.ConsortiumPoolCode;
      params.IsConsortiumPool = this.state.poolCompanyData.IsConsortiumPool;
    }

    this.setState({ isSaving: true })
    this.subscriptions.add(
      PoolMemberAdd(params).subscribe(
        ((result) => {
          this.setState({ isSaving: false })
          if (Number(result) > 0) {
            toast.success("Added member successfully.")
            this.resetState()
            this.props.onAdd()
          } else {
            toast.error(result)
          }
        }),
        error => toast.error(error)
      )
    )
  }

  isValid = (state: AddPoolMemberModalValidity) => state.isCompanyValid && state.isLastNameValid && state.isFirstNameValid && state.isStateValid
  validateState = () => {
    if (this.state.idType !== "DriverID") {
      return true
    }
    if (this.state.stateCode && this.state.stateCode?.trim().length > 0) {
      return true
    }
    return false
  }

  handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const changes: AddPoolMemberModalValidity = {
      isCompanyValid: !!this.state.selectedCompany,
      isLastNameValid: this.state.lastName.trim().length > 0,
      isFirstNameValid: this.state.firstName.trim().length > 0,
      isStateValid: this.validateState()
    }
    const isValid = this.isValid(changes)

    if (isValid) {
      this.savePoolMember()
    } else {
      this.setState(changes)
    }
  }

  resetState = () => this.setState({ ...this.emptyData, ...this.positiveValidity, ...this.personnelCheckEmptyData, showAddPersonnelPanel: false })

  handleClose = () => {
    this.resetState()
    this.props.onCancel()
  }

  handleAddMember = ()  => {
    if (this.state.idType === "DriverID") {
      this.loadStates()
    }
    this.setState({ showAddPersonnelPanel: true })
  }

  handleMemberSearch = (field: string, text: string) => {
    text = text.replace(/\s+/g, "")
    if (field.length === 0 || text.length === 0) {
      toast.error("Please enter proper search criteria.")
      return
    }
    if (field === "SSN") {
      text = text.replace(/\D/g, "")
      if (text.length !== 9) {
        toast.error("Please enter a valid SSN.")
        return
      }
    }
    this.setState({ isLoading: true })

    this.subscriptions.add(
      PoolMemberCheck(this.props.poolID, this.props.accountType, text, field).subscribe(
        (result: CheckPoolMember) => {
          toast.success(result.Message)
          result.ID ? this.setState({ firstName: result.FirstName, lastName: result.LastName, id: text, idType: field, isPersonnelFound: true, isLoading: false, checkMember: result }) : this.setState({ isPersonnelFound: false, isLoading: false })
        },
        (error) => {
          toast.error(error?.errorData?.message || error.message)
          this.setState({ isLoading: false, checkMember: undefined })
          if (error?.errorData?.message) {
            this.setState({ isPersonnelFound: true, id: text, idType: field })
          }
        }
      )
    )
  }

  formatSearchString = (ID: string, value: string): string => {
    return ID === "SSN" ? FormatSSN(value) : value
  }

  handleCompanyChange = (companies: AutoFilterCompany[]) => {
    if (companies.length > 0) {
      this.setState({ selectedCompany: companies[0], isCompanyValid: true, selectedRegion: undefined, selectedLocation: undefined, })
      this.loadAllRegions(companies[0])
      this.loadAllLocations(companies[0], undefined)
    } else {
      this.setState({ isRegionsLoading: false, regions: [], locations: [], selectedCompany: undefined, selectedRegion: undefined, selectedLocation: undefined, isCompanyValid: false, checkMember: undefined })
    }
  }

  handleRegionChange = (regions: AutoFilterRegion[]) => {
    if (regions.length > 0) {
      this.setState({ selectedRegion: regions[0], selectedLocation: undefined })
      this.loadAllLocations(this.state.selectedCompany, regions[0])
    } else {
      this.setState({ isLocationsLoading: false, locations: [], selectedRegion: undefined, selectedLocation: undefined })
    }
  }
  handleLocationChange = (locations: AutoFilterLocation[]) => this.setState({ selectedLocation: locations[0] })
  handleRegulatoryModeChange = (items: PoolRegulatoryMode[]) => {
    if (items.length > 0) {
      this.setState({ selectedRegulatoryMode: items[0], selectedCategory: undefined })
      this.loadCategories(items[0])
    } else {
      this.setState({ categories: [], selectedRegulatoryMode: undefined, selectedCategory: undefined })
    }
  }
  handleCategoryChange = (items: Category[]) => this.setState({ selectedCategory: items[0] })
  handleStateChange = (state: State) => this.setState({ state: state, stateCode: state.Abbreviation, isStateValid: state.Abbreviation.length > 0 })

  render() {
    return <Modal backdrop="static" size="lg" centered show={this.props.show} onHide={this.handleClose} >
      <Modal.Header closeButton>
        <Modal.Title>ADD INDIVIDUAL</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container fluid hidden={this.state.showAddPersonnelPanel}>
          <Row>
            <Col>
              <h5>Check For Existing Personnel</h5>
              <SearchBar searchFields={this.idTypes} defaultField={this.idTypes.find((el) => el.ID === this.state.selectedCompany?.PrimaryIDType) || this.idTypes[3]} resetButton={false} onSearch={this.handleMemberSearch} formatValue={this.formatSearchString} loading={this.state.isLoading} />
            </Col>
          </Row>
          <Row className="modal-footer border-top-0">
            <Button variant="secondary" onClick={this.props.onCancel}>CANCEL</Button>
            <Button variant="primary" onClick={() => this.handleAddMember()} disabled={!this.state.isPersonnelFound}>ADD</Button>
          </Row>
        </Container>
        <Form onSubmit={this.handleFormSubmit} hidden={!this.state.showAddPersonnelPanel}>
          <Row><Col><h5>Create and Add New Pool Member</h5></Col></Row>
          <Form.Row className="form-group required">
            <Form.Label column sm={3}>ID</Form.Label>
            <Col md={9} className="form-label col-form-label">{this.state.id}</Col>
          </Form.Row>
          {this.state.idType === "DriverID" && <Form.Row className="form-group required">
            <Form.Label column sm={3}>DRIVER'S LICENSE STATE OF ISSUANCE</Form.Label>
            <Col md={9}>
              <StateSelect
                isInvalid={!this.state.isStateValid}
                isDisabled={this.state.statesLoading}
                states={this.state.states}
                dropButtonIfInvalid
                selected={this.state.state}
                selectedCode={this.state.stateCode}
                onChange={(state) => this.handleStateChange(state)} />
            </Col>
          </Form.Row>}
          <Form.Row className="form-group required">
            <Form.Label column sm={3}>FIRST NAME</Form.Label>
            <Col md={9}>
              <Form.Control type="text" isInvalid={!this.state.isFirstNameValid} maxLength={20} value={this.state.firstName} onChange={(e) => { this.setState({ firstName: e.target.value, isFirstNameValid: e.target.value.trim().length > 0 }) }} />
            </Col>
          </Form.Row>
          <Form.Row className="form-group required">
              <Form.Label column sm={3}>LAST NAME</Form.Label>
              <Col md={9}>
                <Form.Control type="text" isInvalid={!this.state.isLastNameValid} maxLength={20} value={this.state.lastName} onChange={(e) => { this.setState({ lastName: e.target.value, isLastNameValid: e.target.value.trim().length > 0 }) }} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={3}>COMPANY</Form.Label>
              { this.state.companies.length <= 1 && <Row><Col md={9}>{this.state.companies[0]?.Name}</Col></Row> }
              { this.state.companies.length > 1 &&
                <Col md={9}>
                  <CompanyInput filter onChange={ this.handleCompanyChange } placeholder="Select" />
                </Col>}
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column sm={3}>REGION</Form.Label>
              {!this.state.isRegionsLoading && this.state.regions.length === 0 && <Form.Label column sm={9}>None</Form.Label>}
              {(this.state.isRegionsLoading || this.state.regions.length > 0) &&
                <Col md={9}>
                  <Typeahead id="region"
                    ref={this.regionTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    isLoading={this.state.isRegionsLoading}
                    onChange={this.handleRegionChange}
                    options={this.state.regions}
                    selected={this.state.selectedRegion ? [this.state.selectedRegion] : []}
                    minLength={0}
                    emptyLabel="No Regions Found"
                    placeholder="Select"
                    inputProps={{ autoComplete: "chrome-off" }}>
                    {!this.state.isRegionsLoading &&
                      <Button variant="toggle" onClick={() => { this.regionTypeahead.current?.toggleMenu() }}>
                        <FaAngleDown />
                      </Button>}
                  </Typeahead>
                </Col>}
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column sm={3}>LOCATION</Form.Label>
              {!this.state.isLocationsLoading && this.state.locations.length === 0 && <Form.Label column sm={9}>None</Form.Label>}
              {(this.state.isLocationsLoading || this.state.locations.length > 0) &&
                <Col md={9}>
                  <Typeahead id="location"
                    ref={this.locationTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    isLoading={this.state.isLocationsLoading}
                    onChange={this.handleLocationChange}
                    options={this.state.locations}
                    selected={this.state.selectedLocation ? [this.state.selectedLocation] : []}
                    minLength={0}
                    emptyLabel="No Locations Found"
                    placeholder="Select">
                    {!this.state.isLocationsLoading &&
                      <Button variant="toggle" onClick={() => { this.locationTypeahead.current?.toggleMenu() }}>
                        <FaAngleDown />
                      </Button>}
                  </Typeahead>
                </Col>}
            </Form.Row>
            <Form.Row className="form-group" hidden={!!!this.state.poolCompanyData?.HasRegModeBlock}>
              <Form.Label column sm={3}>REGULATORY MODE</Form.Label>
              {!this.state.isLoading && this.state.regulatoryModes.length === 0 && <Form.Label column sm={9}>None</Form.Label>}
              {(this.state.isLoading || this.state.regulatoryModes.length > 0) &&
                <Col md={9}>
                  <Typeahead id="regMode"
                    ref={this.regulatoryModeTypeahead}
                    labelKey="Description"
                    filterBy={(option, props) => props.selected.length > 0 || option?.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    onChange={this.handleRegulatoryModeChange}
                    disabled={this.state.isLoading}
                    options={this.state.regulatoryModes}
                    selected={this.state.selectedRegulatoryMode ? [this.state.selectedRegulatoryMode] : []}
                    emptyLabel="No Regulatory Modes Found"
                    placeholder="Select">
                    <Button variant="toggle" onClick={() => { this.regulatoryModeTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>
                  </Typeahead>
                </Col>}
            </Form.Row>
            <Form.Row className="form-group" hidden={!!!this.state.poolCompanyData?.HasRegModeBlock}>
              <Form.Label column sm={3}>CATEGORY</Form.Label>
              {!this.state.isCategoriesLoading && this.state.categories.length === 0 && <Form.Label column sm={9}>None</Form.Label>}
              {(this.state.isCategoriesLoading || this.state.categories.length > 0) &&
                <Col md={9}>
                  <Typeahead id="category"
                    ref={this.categoryTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    onChange={this.handleCategoryChange}
                    disabled={this.state.isCategoriesLoading}
                    options={this.state.categories}
                    selected={this.state.selectedCategory ? [this.state.selectedCategory] : []}
                    emptyLabel="No Categories Found"
                    placeholder="Select">
                    <Button variant="toggle" onClick={() => { this.categoryTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>
                  </Typeahead>
                </Col>}
            </Form.Row>
            <Form.Row className="form-group" hidden={!!!this.state.poolCompanyData?.IsConsortiumPool}>
              <Form.Label column sm={3} className="text-uppercase">{this.state.poolCompanyData?.User9Alias}</Form.Label>
              <Form.Label column sm={9}>{this.state.poolCompanyData?.ConsortiumPoolCode}</Form.Label>
            </Form.Row>
            <Form.Row className="modal-footer">
              <Button variant="secondary" onClick={() => this.resetState()}>CANCEL</Button>
              <Button variant="primary" type="submit" disabled={!this.isValid(this.state)} >
                {this.state.isSaving ? <Spinner animation="border" size="sm" /> : "SAVE"}
              </Button>
            </Form.Row>
        </Form>
      </Modal.Body>
    </Modal>
  }
}
